import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import SortIcon from '@mui/icons-material/Sort';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Page } from 'components/Page';
import COLORS from 'theme/colors';
import { ROUTE } from 'route/RouteEnums';
import { useAuth } from 'utils/AuthContext';
import { Locations, toAssignedProviderId } from 'utils/dropDownList';
import { getPatientDetailsAPI, getSelfSchedulingEligibilityAPI } from 'services/profileServices';
import {
  PATIENT_LOOKUP_FAILED_ERROR_CODE,
  PATIENT_LOOKUP_FAILED_MESSAGE,
  SCHEDULING_UNAVAILABLE_MESSAGE,
} from 'shared/content/constants';
import {
  bookSelfSchedulingAppointmentAPI,
  getSelfSchedulingLocationsAPI,
  getSelfSchedulingPatientContextAPI,
  getSelfSchedulingProvidersAPI,
  PatientOnboardingStatus,
  rescheduleSelfSchedulingAppointmentAPI,
} from 'services/selfSchedulingServices';
import FiltersPanel, { describeFilters, ProviderAvailabilityNavState, SelfSchedulingFilters } from './FiltersPanel';
import ProviderCard from './ProviderCard';
import SelectionConfirmDialog from './SelectionConfirmDialog';
import InPersonLocationRestrictedDialog from './InPersonLocationRestrictedDialog';
import ProviderUnavailableDialog from './ProviderUnavailableDialog';
import { AvailabilitySlot, PatientSelfSchedulingContext, Provider } from './types';
import { formatSlotLocation, mapOnboardingStatus, mapPatientContext, mapProvider, toScheduledTime } from './mappers';

type SortOption = 'soonest' | 'name';

interface OfficeLocation {
  id: number;
  name: string;
  city: string;
}

// Virtual is selected by default on first load (not an empty/no-filter state) - "Clear filters"
// still clears consultation type to "all" (see handleClearFilters), this is only the initial
// value before the patient has touched the filter panel at all.
const DEFAULT_FILTERS: SelfSchedulingFilters = {
  consultationTypes: ['Virtual'],
  locations: [],
};

// Clinic locations are a fixed, hand-maintained list (same one the patient profile's
// preferred-location dropdown uses) - no reason to fetch it from the backend. Always the full
// list, every entry selectable - the In-person + IL-preferred restriction is enforced when a
// selection is made (see handleToggleFilter), not by disabling entries.
const ALL_LOCATION_OPTIONS = Locations.map((location) => location.filterValue);

// Filter values of every non-Illinois (Missouri) clinic - an Illinois-preferred patient can't book
// In-person at any of these (see isRestrictedInPersonSelection).
const NON_IL_LOCATION_OPTIONS = Locations.filter((location) => location.state !== 'IL').map(
  (location) => location.filterValue
);

// The office's real city (backend's own OfficeLocation.city, see fetchProviders below) doesn't
// always match this clinic's filter display name (Locations.filterValue above) - the Richmond
// Heights office is filed under "St. Louis" on the backend, so matching by city name alone
// would never find it and would silently show zero providers for that filter. Extend this map
// whenever another clinic's display name and backend city name diverge the same way.
const FILTER_TO_BACKEND_CITY: Record<string, string> = {
  'Richmond Heights': 'St. Louis',
};

const earliestSlotTimestamp = (provider: Provider): number => {
  const timestamps = provider.availability
    .map((slot) => new Date(slot.start).getTime())
    .filter((value) => !Number.isNaN(value));
  return timestamps.length ? Math.min(...timestamps) : Infinity;
};

const SEARCH_DEBOUNCE_MS = 300;

// A successful book/reschedule now flips the self-scheduling free-slot row and mirrors the
// change into ehr-service in the background (SelfSchedulingService, fire-and-forget - see
// spec) rather than before responding, so the page this navigates to next (Dashboard/Manage
// Appointment, both reading ehr-service's own appointment data) could otherwise render with
// stale data if it loads before that background work finishes. Holding here (bookingLoading's
// own Backdrop stays up the whole time) for a few seconds first gives it a head start, same
// trade-off as the backend's own deliberate move to async - a short, bounded wait here instead
// of every downstream read needing its own retry/poll for eventual consistency.
const POST_BOOKING_NAVIGATE_DELAY_MS = 5000;

// Office locations rarely change, so cache them client-side and only refetch once the
// cache is a day old, instead of hitting the backend on every page visit.
const OFFICE_LOCATIONS_CACHE_KEY = 'selfScheduling.officeLocations';
const OFFICE_LOCATIONS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const readCachedOfficeLocations = (): OfficeLocation[] | null => {
  try {
    const raw = localStorage.getItem(OFFICE_LOCATIONS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.data) || Date.now() - parsed?.timestamp >= OFFICE_LOCATIONS_CACHE_TTL_MS) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
};

const writeCachedOfficeLocations = (data: OfficeLocation[]) => {
  try {
    localStorage.setItem(OFFICE_LOCATIONS_CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // Storage full/unavailable - caching is a nice-to-have, not required for correctness.
  }
};

const SelfScheduling = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Set when arriving back from the single-provider calendar's "Back" button (see
  // ProviderAvailability's backState) - restores exactly what was selected before "More" was
  // clicked, instead of the default-location effect below resetting to the patient's default.
  // Also set when ManageAppointment sends a new patient (no assigned provider) straight here to
  // reschedule instead of book - see spec/2026-09-18-MM-1067-new-patient-cross-provider-
  // reschedule.md.
  const restoredNavState = location.state as
    | { filters?: SelfSchedulingFilters; searchText?: string; reschedule?: ProviderAvailabilityNavState['reschedule'] }
    | undefined;
  const reschedule = restoredNavState?.reschedule;
  const { user } = useAuth();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  // Drives the providers fetch directly - picking a filter option (see handleToggleFilter)
  // applies immediately, no separate Apply step; the providers list's own loading state
  // (providersLoading) covers the gap until the new results land.
  const [filters, setFilters] = useState<SelfSchedulingFilters>(DEFAULT_FILTERS);
  const [locationSearch, setLocationSearch] = useState('');
  const [sortOption, setSortOption] = useState<SortOption>('soonest');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [selection, setSelection] = useState<{ provider: Provider; slot: AvailabilitySlot } | null>(null);
  // The providers call always needs a resolved location (see the effect below), so it stays
  // gated behind this until then - we never want to fetch/compute free slots across every
  // clinic location at once.
  const [resolvingDefaultLocation, setResolvingDefaultLocation] = useState(true);
  const [defaultLocation, setDefaultLocation] = useState<string | null>(null);
  // The state (e.g. "IL") behind the patient's own preferred clinic - only used to refuse an
  // In-person + non-IL clinic selection when this is "IL" (see isRestrictedInPersonSelection); any
  // other preferred state, or Virtual, has no restriction.
  // Not the same thing as officeLocations, which resolves a display name to a real office id,
  // not a state.
  const [preferredState, setPreferredState] = useState<string | null>(null);
  // Open while showing why an In-person + Missouri-clinic selection wasn't applied for an
  // Illinois-preferred patient (see handleToggleFilter).
  const [inPersonRestrictionOpen, setInPersonRestrictionOpen] = useState(false);
  // Bumped each time a filter selection is refused - FiltersPanel re-mounts its radio groups on it,
  // so the refused option doesn't stay visibly checked (see FiltersPanel's selectionResetKey).
  const [refusedSelectionCount, setRefusedSelectionCount] = useState(0);
  const defaultLocationResolvedRef = useRef(false);
  // The displayed location names are a fixed hardcoded list, but the backend filters by real
  // office id, so this is fetched once purely to translate the selected name to its id.
  const [officeLocations, setOfficeLocations] = useState<OfficeLocation[]>([]);
  const [officeLocationsLoading, setOfficeLocationsLoading] = useState(true);
  // payMode (and canBookAppointment) - resolved once up front, then sent explicitly on every
  // providers/booking call below (see PatientSelfSchedulingContextDTO on the backend).
  const [patientContext, setPatientContext] = useState<PatientSelfSchedulingContext | null>(null);
  const [contextLoading, setContextLoading] = useState(true);
  // Feeds the NEW-patient onboarding blackout (see PatientOnboardingStatus's own doc) - resolved
  // once up front, same as patientContext above, then sent on every providers call below. Has no
  // effect for an existing patient either way, so this is fetched unconditionally rather than
  // gated on patient type (the backend already only applies it to a NEW patient).
  const [onboardingStatus, setOnboardingStatus] = useState<PatientOnboardingStatus | undefined>(undefined);

  useEffect(() => {
    if (!user?.patientId) {
      setContextLoading(false);
      return;
    }
    getSelfSchedulingPatientContextAPI(user.patientId)
      .then((response: any) => setPatientContext(mapPatientContext(response?.data)))
      .catch(() => setPatientContext(mapPatientContext(undefined)))
      .finally(() => setContextLoading(false));
    getSelfSchedulingEligibilityAPI(user.patientId)
      .then((response: any) => setOnboardingStatus(mapOnboardingStatus(response?.data)))
      .catch(() => setOnboardingStatus(mapOnboardingStatus(undefined)));
  }, [user?.patientId]);

  // Every filter/search change is a fresh call to the backend (which pulls current free
  // slots from the EHR per matching provider) rather than filtering an already-fetched list.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Resolve a single starting location - the patient's preferred clinic if it matches one of
  // the known locations, otherwise the first one - before the providers call ever fires, so
  // that call (and the free-slot computation behind it) is always scoped to one location
  // instead of every office at once.
  useEffect(() => {
    if (defaultLocationResolvedRef.current) return;
    defaultLocationResolvedRef.current = true;

    const restoredFilters = restoredNavState?.filters;
    const restoredLocation = restoredFilters?.locations[0];
    if (restoredFilters && restoredLocation) {
      setFilters(restoredFilters);
      setSearchText(restoredNavState?.searchText ?? '');
      setDefaultLocation(restoredLocation);
      setResolvingDefaultLocation(false);
      return;
    }

    const fallback = ALL_LOCATION_OPTIONS[0] ?? null;

    const applyDefault = (location: string | null) => {
      setDefaultLocation(location);
      if (location) {
        setFilters((prev) => ({ ...prev, locations: [location] }));
      }
      setResolvingDefaultLocation(false);
    };

    if (!user?.patientId) {
      applyDefault(fallback);
      return;
    }
    getPatientDetailsAPI(user.patientId)
      .then((response: any) => {
        // An existing patient already has a primary provider assigned (set by staff via
        // Admin > Patient Info > Details) - skip the directory entirely and go straight to
        // that provider's calendar (every office they work at, Virtual only) instead of
        // making them search for someone they're already established with.
        // MM-1136: "No assigned provider" is read as null, so the patient gets the directory.
        const assignedProviderId = toAssignedProviderId(response?.data?.providerId);
        if (assignedProviderId) {
          navigate(ROUTE.SELF_SCHEDULING_PROVIDER.replace(':providerId', String(assignedProviderId)), {
            replace: true,
            state: {
              filters: { consultationTypes: ['Virtual'], locations: [] },
              searchText: '',
              directEntry: true,
            },
          });
          return;
        }
        const preferredAddress = response?.data?.preferredLocation;
        const preferredLocationEntry = Locations.find((location) => location.location === preferredAddress);
        setPreferredState(preferredLocationEntry?.state ?? null);
        applyDefault(preferredLocationEntry?.filterValue ?? fallback);
      })
      .catch(() => applyDefault(fallback));
  }, [user?.patientId, restoredNavState?.filters, restoredNavState?.searchText]);

  // The location list is independent of everything else above, so it loads in parallel -
  // it only exists to resolve the selected display name to a real office id below.
  useEffect(() => {
    const cached = readCachedOfficeLocations();
    if (cached) {
      setOfficeLocations(cached);
      setOfficeLocationsLoading(false);
      return;
    }
    getSelfSchedulingLocationsAPI()
      .then((response: any) => {
        const data: OfficeLocation[] = response?.data ?? [];
        setOfficeLocations(data);
        writeCachedOfficeLocations(data);
      })
      .catch(() => {
        toast.error('Unable to load locations. Please try again.');
      })
      .finally(() => setOfficeLocationsLoading(false));
  }, []);

  // Also called after a booking fails because the slot was taken (see handleConfirmSelection
  // below), so the now-stale slot disappears from the list without a full page reload.
  const fetchProviders = () => {
    if (resolvingDefaultLocation || officeLocationsLoading || contextLoading) return;
    // Already known to have an active/upcoming appointment - skip the (expensive) providers
    // fetch entirely, the blocked-state message below covers the page instead. A reschedule is
    // managing that same appointment, not booking a second one, so this gate doesn't apply then.
    if (!reschedule && patientContext && !patientContext.canBookAppointment) return;
    const selectedCity = filters.locations[0];
    const backendCity = (selectedCity && FILTER_TO_BACKEND_CITY[selectedCity]) || selectedCity;
    const matchedOffice = officeLocations.find(
      (office) => office.city?.trim().toLowerCase() === backendCity?.trim().toLowerCase()
    );
    // No matching office id for the selected name - rather than falling back to "no filter"
    // (which would fetch/compute free slots across every location), show no results.
    if (!matchedOffice) {
      setProviders([]);
      return;
    }
    setProvidersLoading(true);
    getSelfSchedulingProvidersAPI({
      locationIds: [matchedOffice.id],
      consultationType: filters.consultationTypes[0],
      payMode: patientContext?.payMode ?? 'InsurancePay',
      search: debouncedSearchText,
      patientId: user?.patientId,
      onboardingStatus,
    })
      .then((response: any) => {
        const mapped: Provider[] = (response?.data ?? []).map(mapProvider);
        setProviders(mapped);
      })
      .catch(() => {
        toast.error('Unable to load providers. Please try again.');
      })
      .finally(() => setProvidersLoading(false));
  };

  useEffect(() => {
    fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters,
    debouncedSearchText,
    resolvingDefaultLocation,
    officeLocationsLoading,
    officeLocations,
    contextLoading,
    patientContext,
    onboardingStatus,
  ]);

  // Both consultationTypes and locations are radio groups - exactly one of each is ever
  // active, so picking one replaces the whole selection rather than adding to it. Applies
  // immediately (updates filters directly, which the providers-fetch effect below is keyed
  // on) - no separate Apply step; providersLoading covers the gap until the new results land.
  // An Illinois-preferred patient can't book In-person at a Missouri clinic. Checked here, on the
  // patient's own selection (not on load, and nothing is greyed out): picking a Missouri clinic
  // while In-person is selected, or In-person while a Missouri clinic is selected, opens
  // InPersonLocationRestrictedDialog and the selection is simply not applied - filters stay as they
  // were, so the previous option stays selected and providers aren't re-fetched. UI-only, as before. The
  // radio groups are re-mounted (refusedSelectionCount) so the refused option is unchecked right
  // away, not left looking selected behind (or after closing) the dialog.
  const isRestrictedInPersonSelection = (next: SelfSchedulingFilters) =>
    preferredState === 'IL' &&
    next.consultationTypes[0] === 'In-person' &&
    !!next.locations[0] &&
    NON_IL_LOCATION_OPTIONS.includes(next.locations[0]);

  const handleToggleFilter = (category: keyof SelfSchedulingFilters, value: string) => {
    const next = { ...filters, [category]: [value] };
    if (isRestrictedInPersonSelection(next)) {
      setRefusedSelectionCount((count) => count + 1);
      setInPersonRestrictionOpen(true);
      return;
    }
    setFilters(next);
  };

  const handleClearFilters = () => {
    // Both are radio groups, so "clear" means reset to their defaults rather than an empty
    // (nothing-selected) list.
    const cleared = {
      consultationTypes: DEFAULT_FILTERS.consultationTypes,
      locations: defaultLocation ? [defaultLocation] : [],
    };
    setFilters(cleared);
    setLocationSearch('');
    setSearchText('');
  };

  const handleSelectSlot = (provider: Provider, slot: AvailabilitySlot) => {
    setSelection({ provider, slot });
  };

  const handleCancelSelection = () => {
    setSelection(null);
  };

  const handleConfirmSelection = () => {
    if (!selection) return;
    const { provider, slot } = selection;
    const payMode = patientContext?.payMode ?? 'InsurancePay';
    setBookingLoading(true);

    if (reschedule) {
      rescheduleSelfSchedulingAppointmentAPI(reschedule.appointmentId, {
        patientId: reschedule.currentSlot.patientId,
        duration: slot.duration,
        payMode,
        current: {
          doctor: reschedule.currentSlot.providerId,
          office: reschedule.currentSlot.officeId,
          profileId: reschedule.currentSlot.profileId,
          examRoom: reschedule.currentSlot.examRoom,
          scheduledTime: reschedule.currentSlot.scheduledTime,
        },
        replacement: {
          doctor: provider.id,
          office: slot.officeId,
          profileId: slot.profileId[0],
          examRoom: slot.examRoom,
          scheduledTime: toScheduledTime(slot.start),
        },
      })
        .then((response: any) => {
          if (response?.data?.apiResult) {
            setSelection(null);
            toast.success('Appointment rescheduled!');
            // bookingLoading deliberately stays true (Backdrop still up) through this hold -
            // see POST_BOOKING_NAVIGATE_DELAY_MS's own doc. Not cleared afterward either: this
            // component is about to unmount on navigate, so there's nothing left to update.
            setTimeout(() => navigate(ROUTE.MANAGE_APPOINTMENT), POST_BOOKING_NAVIGATE_DELAY_MS);
            return;
          }
          if (response?.data?.slotUnavailable) {
            // Same race as booking: someone else took this slot between it being listed and
            // this request landing. The original appointment is untouched (DrChrono rejected
            // the change), so just refresh this list rather than navigating anywhere.
            setSelection(null);
            toast.error('That time slot was just booked. Please choose another available slot.');
            fetchProviders();
          } else if (response?.data?.errorCode === PATIENT_LOOKUP_FAILED_ERROR_CODE) {

            // patient-mgmt lookup failed - nothing was booked/changed; the patient can only retry later.

            toast.error(PATIENT_LOOKUP_FAILED_MESSAGE);

          } else if (response?.data?.throttled) {
            toast.error(SCHEDULING_UNAVAILABLE_MESSAGE);
          } else {
            toast.error(response?.data?.messsage ?? 'Failed to reschedule appointment');
          }
          setBookingLoading(false);
        })
        .catch(() => {
          toast.error('Failed to reschedule appointment. Please try again.');
          setBookingLoading(false);
        });
      return;
    }

    bookSelfSchedulingAppointmentAPI({
      patientId: user?.patientId,
      doctor: provider.id,
      office: slot.officeId,
      // slot.profileId can carry more than one candidate profile (a cash-pay/insurance-pay pair
      // sharing an identical schedule) - the backend resolves the correct one from the patient's
      // pay-type, so any one entry is fine to send as a starting point (see
      // SelfSchedulingService.resolveProfileForBooking).
      profileId: slot.profileId[0],
      examRoom: slot.examRoom,
      duration: slot.duration,
      scheduledTime: toScheduledTime(slot.start),
      payMode,
    })
      .then((response: any) => {
        if (response?.data?.apiResult) {
          setSelection(null);
          toast.success('Appointment confirmed!');
          // bookingLoading deliberately stays true (Backdrop still up) through this hold - see
          // POST_BOOKING_NAVIGATE_DELAY_MS's own doc. Not cleared afterward either: this
          // component is about to unmount on navigate, so there's nothing left to update.
          setTimeout(() => navigate(ROUTE.DASHBOARD), POST_BOOKING_NAVIGATE_DELAY_MS);
          return;
        }
        if (response?.data?.slotUnavailable) {
          // Someone else booked this slot between it being listed and this request landing.
          // Stay on this screen (don't navigate away) so the patient can pick another slot -
          // close the stale confirm dialog and refresh so the taken slot disappears from view.
          setSelection(null);
          toast.error('That time slot was just booked. Please choose another available slot.');
          fetchProviders();
        } else if (response?.data?.errorCode === PATIENT_LOOKUP_FAILED_ERROR_CODE) {

          // patient-mgmt lookup failed - nothing was booked/changed; the patient can only retry later.

          toast.error(PATIENT_LOOKUP_FAILED_MESSAGE);

        } else if (response?.data?.throttled) {
          toast.error(SCHEDULING_UNAVAILABLE_MESSAGE);
        } else {
          toast.error(response?.data?.messsage ?? 'Failed to book appointment');
        }
        setBookingLoading(false);
      })
      .catch(() => {
        toast.error('Failed to book appointment. Please try again.');
        setBookingLoading(false);
      });
  };

  const handleMore = (provider: Provider) => {
    // The list is already scoped to one selected clinic (see fetchProviders) - the calendar
    // needs that same real office id, not just the displayed location name, to stay scoped to
    // it too instead of showing every office the provider works at.
    const selectedCity = filters.locations[0];
    const backendCity = (selectedCity && FILTER_TO_BACKEND_CITY[selectedCity]) || selectedCity;
    const matchedOffice = officeLocations.find(
      (office) => office.city?.trim().toLowerCase() === backendCity?.trim().toLowerCase()
    );
    navigate(ROUTE.SELF_SCHEDULING_PROVIDER.replace(':providerId', String(provider.id)), {
      state: { filters, searchText, provider, officeId: matchedOffice?.id, reschedule },
    });
  };

  const activeFilterSummary = useMemo(() => describeFilters(filters), [filters]);

  const sortedProviders = useMemo(() => {
    // Filtering (location, consultation type, name search) already happened server-side.
    const sorted = [...providers];
    if (sortOption === 'name') {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      sorted.sort((a, b) => earliestSlotTimestamp(a) - earliestSlotTimestamp(b));
    }
    return sorted;
  }, [providers, sortOption]);

  const renderFiltersPanel = () => (
    <FiltersPanel
      filters={filters}
      locations={ALL_LOCATION_OPTIONS}
      locationSearch={locationSearch}
      onLocationSearchChange={setLocationSearch}
      onToggle={handleToggleFilter}
      onClearFilters={handleClearFilters}
      selectionResetKey={refusedSelectionCount}
    />
  );

  let providerListContent;
  if (providersLoading || resolvingDefaultLocation || officeLocationsLoading) {
    providerListContent = (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (sortedProviders.length === 0) {
    providerListContent = (
      <Box
        sx={{
          border: `1px dashed ${COLORS.Gray300}`,
          borderRadius: '12px',
          p: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant='body1' sx={{ color: COLORS.Gray600 }}>
          No providers match your current filters. Try clearing some filters or searching for something else.
        </Typography>
      </Box>
    );
  } else {
    providerListContent = (
      <Grid container spacing={3}>
        {sortedProviders.map((provider) => (
          <Grid item xs={12} sm={6} key={provider.id}>
            <ProviderCard provider={provider} onSelectSlot={handleSelectSlot} onMore={handleMore} />
          </Grid>
        ))}
      </Grid>
    );
  }

  /**
   * MM-1161: until the patient-details lookup above settles, this page doesn't yet know whether
   * it's the directory at all - an established patient (assigned primary provider) is redirected
   * straight to that provider's calendar. Render only a bare spinner (no title, filters or
   * heading) until then, so the directory never flashes up before that redirect.
   */
  if (resolvingDefaultLocation) {
    return (
      <Page>
        <Box sx={{ textAlign: 'center', p: 6 }}>
          <CircularProgress size={32} />
        </Box>
      </Page>
    );
  }

  /*
   * MM-1136: an existing patient set to "No assigned provider" (or whose primary provider is no
   * longer available) can't book online - show the call-to-schedule dialog, whose OK goes back to
   * the dashboard, the same as ProviderAvailability. Booking only - a reschedule is unaffected.
   */
  if (!reschedule && !contextLoading && patientContext?.primaryProviderUnavailable) {
    return (
      <Page title='Schedule Appointment'>
        <ProviderUnavailableDialog open onOk={() => navigate(ROUTE.DASHBOARD)} />
      </Page>
    );
  }

  let mainContent;
  if (contextLoading) {
    mainContent = (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (!reschedule && patientContext && !patientContext.canBookAppointment) {
    mainContent = (
      <Box
        sx={{
          border: `1px dashed ${COLORS.Gray300}`,
          borderRadius: '12px',
          p: 4,
          textAlign: 'center',
          backgroundColor: COLORS.White,
        }}
      >
        <Typography variant='body1' sx={{ color: COLORS.Gray600, mb: 2 }}>
          You already have an upcoming appointment. You can book another one once it has passed.
        </Typography>
        <Button
          variant='contained'
          onClick={() => navigate(ROUTE.MANAGE_APPOINTMENT)}
          sx={{ textTransform: 'none', backgroundColor: COLORS.DarkBlue }}
        >
          View My Appointments
        </Button>
      </Box>
    );
  } else {
    mainContent = (
      <>
        {reschedule ? (
          <Box
            sx={{
              border: `1px solid ${COLORS.Gray300}`,
              borderRadius: '12px',
              backgroundColor: COLORS.White,
              p: 2,
              mb: 2,
            }}
          >
            <Typography variant='body2' sx={{ color: COLORS.Gray600 }}>
              Rescheduling your appointment with <b>{reschedule.providerName}</b> on{' '}
              {reschedule.currentSummary.day} &middot; {reschedule.currentSummary.time}
              {reschedule.currentSummary.officeAddress ? ` at ${reschedule.currentSummary.officeAddress}` : ''}.
              Pick a new provider, time, or location below to replace it.
            </Typography>
          </Box>
        ) : null}

        <TextField
          fullWidth
          placeholder='Search Provider'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ mb: 2, backgroundColor: COLORS.White, borderRadius: '8px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{ color: COLORS.Gray600 }} />
              </InputAdornment>
            ),
          }}
        />

        {activeFilterSummary ? (
          <Typography variant='body2' sx={{ color: COLORS.Gray600, mb: 2 }}>
            {activeFilterSummary}
          </Typography>
        ) : null}

        <Box
          sx={{
            display: 'flex',
            // On mobile this row holds both Filter and Sort (spaced to opposite ends); on
            // desktop the Filter button is hidden, leaving Sort as the row's only item -
            // 'space-between' has nothing to space it from, so it needs 'center' here or it
            // just sits wherever its own margin pushes it (previously flush-right).
            justifyContent: { xs: 'space-between', md: 'center' },
            alignItems: 'center',
            mb: 3,
            gap: 1,
            flexWrap: 'nowrap',
          }}
        >
          <Button
            variant='outlined'
            startIcon={<TuneIcon />}
            onClick={() => setFilterDrawerOpen(true)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              textTransform: 'none',
              borderColor: COLORS.Gray300,
              color: COLORS.Text,
              flexShrink: 0,
              px: { xs: 1.5, sm: 2 },
            }}
          >
        Filter
          </Button>
          <Select
            size='small'
            value={sortOption}
            onChange={(e: SelectChangeEvent) => setSortOption(e.target.value as SortOption)}
            renderValue={(value) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <SortIcon fontSize='small' sx={{ color: COLORS.Gray600 }} />
                {value === 'name' ? 'Name A–Z' : 'Earliest available'}
              </Box>
            )}
            sx={{
              minWidth: { xs: 0, sm: 220 },
              // 200 fits "Earliest available" next to the Filter button on a 360px screen
              // without the label truncating to an ellipsis.
              maxWidth: { xs: 200, sm: 'none' },
              borderRadius: '8px',
              ml: { xs: 'auto', md: 0 },
              flexShrink: 1,
            }}
          >
            <MenuItem value='soonest'>Sort: Earliest available</MenuItem>
            <MenuItem value='name'>Sort: Name A&ndash;Z</MenuItem>
          </Select>
        </Box>

        <Typography variant='h3' sx={{ mb: 2, color: COLORS.Gray900 }}>
      Featured Providers
        </Typography>

        {providerListContent}
      </>
    );
  }

  return (
    <Page
      title={reschedule ? 'Reschedule Appointment' : 'Schedule Appointment'}
      backDir={reschedule ? ROUTE.MANAGE_APPOINTMENT : undefined}
    >
      {/* Above theme.zIndex.modal (StyledDialog/SelectionConfirmDialog's own zIndex, 1300 by
          default), not just theme.zIndex.drawer (1200) - SelectionConfirmDialog stays open and
          visible while booking, so this must render in front of it, not behind it. */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }} open={bookingLoading}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            display: { xs: 'none', md: 'block' },
            // Without this, a long location list can push the bottom of the panel far enough
            // down that it drifts out of view as the page scrolls - sticky keeps the whole
            // panel anchored in the filter section on non-mobile screens.
            alignSelf: 'flex-start',
            position: { md: 'sticky' },
            top: { md: 16 },
          }}
        >
          {renderFiltersPanel()}
        </Grid>

        <Grid item xs={12} md={9}>
          <Typography variant='h2' sx={{ mb: 2 }}>
            Find the right mental health provider
          </Typography>

          {mainContent}
        </Grid>
      </Grid>

      <Drawer
        anchor='bottom'
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            maxHeight: '85vh',
            overflowY: 'auto',
            p: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={() => setFilterDrawerOpen(false)} aria-label='close filters'>
            <CloseIcon />
          </IconButton>
        </Box>
        {renderFiltersPanel()}
      </Drawer>

      <InPersonLocationRestrictedDialog open={inPersonRestrictionOpen} onOk={() => setInPersonRestrictionOpen(false)} />

      <SelectionConfirmDialog
        open={!!selection}
        providerName={selection?.provider.name ?? ''}
        day={selection?.slot.day ?? ''}
        time={selection?.slot.time ?? ''}
        location={selection ? formatSlotLocation(selection.slot) : undefined}
        consultationType={filters.consultationTypes[0]}
        onCancel={handleCancelSelection}
        onContinue={handleConfirmSelection}
        title={reschedule ? 'Confirm Reschedule' : undefined}
        bodyPrefix={reschedule ? 'You selected a new time with' : undefined}
        currentAppointment={
          reschedule
            ? {
              providerName: reschedule.providerName ?? '',
              day: reschedule.currentSummary.day,
              time: reschedule.currentSummary.time,
              location: reschedule.currentSummary.officeAddress,
            }
            : undefined
        }
      />
    </Page>
  );
};

export default SelfScheduling;
